import { Box, Flex, type SpaceProps } from "@chakra-ui/react";
import { Section } from "components/section";
import { type BrandColor } from "contexts/color-theme";
import React from "react";
import { TextTitle, type TextTitleProps } from "ui/text";

type SimpleFadeHeroProps = {
  variant: BrandColor;
  shade?: "150" | "50";
  eyebrow?: string;
} & Pick<TextTitleProps, "title" | "description" | "actions" | "maxW" | "descriptionMaxW"> &
  Pick<SpaceProps, "pt" | "pb">;

/**
 * A hero component that includes a very simple linear fade to white, and changes the
 * header color to white when reaching the CTA buttons.
 *
 * This is more performant than using `LightGradientBackground` for pages like catalogs
 * where there are a lot of interactive/animated elements.
 */
export function BasicGradientHero({
  shade = "150",
  variant: color,
  eyebrow,
  title,
  description,
  actions,
  maxW,
  descriptionMaxW,
  pt = { base: "bigger", md: "biggest" },
  pb = { base: "bigger", md: "biggest" },
}: SimpleFadeHeroProps) {
  return (
    <Box as="section">
      <Section
        as="div"
        bg={`new.${color}.${shade}`}
        themeBgColor={`new.${color}.${shade}`}
        py="0"
        pt={pt}
        bgGradient="linear(to-t, #ffffff, transparent)"
      >
        <TextTitle
          size="lg"
          align="center"
          chip={eyebrow ? { text: eyebrow } : undefined}
          titleAs="h1"
          title={title}
          descriptionAs="h2"
          description={description}
          maxW={maxW}
          descriptionMaxW={descriptionMaxW}
        />
      </Section>

      <Section my="0" pt="big" pb={pb}>
        <Flex justifyContent={{ base: "start", md: "center" }}>{actions}</Flex>
      </Section>
    </Box>
  );
}
